import { supabase } from '../lib/supabase';

export const twoFactorService = {
  /**
   * Check if user has 2FA enabled
   */
  async check2FAStatus(userId) {
    const { data, error } = await supabase?.from('user_profiles')?.select('two_factor_enabled, two_factor_method, email_verified, phone_verified')?.eq('id', userId)?.single();

    if (error) throw error;

    return {
      enabled: data?.two_factor_enabled || false,
      method: data?.two_factor_method || 'sms',
      emailVerified: data?.email_verified || false,
      phoneVerified: data?.phone_verified || false
    };
  },

  /**
   * Enable 2FA for user
   */
  async enable2FA(userId, method = 'sms') {
    // Validate method
    if (!['email', 'sms']?.includes(method)) {
      throw new Error('Invalid 2FA method. Must be email or sms');
    }

    // Check verification status based on method
    const { data: profile } = await supabase?.from('user_profiles')?.select('email_verified, phone_verified, phone')?.eq('id', userId)?.single();

    if (method === 'email' && !profile?.email_verified) {
      throw new Error('Email must be verified before enabling 2FA via email');
    }

    if (method === 'sms') {
      if (!profile?.phone) {
        throw new Error('Phone number must be added before enabling 2FA via SMS');
      }
      if (!profile?.phone_verified) {
        throw new Error('Phone number must be verified before enabling 2FA via SMS');
      }
    }

    const { error } = await supabase?.from('user_profiles')?.update({
        two_factor_enabled: true,
        two_factor_method: method,
        updated_at: new Date()?.toISOString()
      })?.eq('id', userId);

    if (error) throw error;

    // Generate backup codes
    const backupCodes = await this.generateBackupCodes(userId);

    return {
      success: true,
      method,
      backupCodes
    };
  },

  /**
   * Disable 2FA for user
   */
  async disable2FA(userId) {
    const { error } = await supabase?.from('user_profiles')?.update({
        two_factor_enabled: false,
        two_factor_backup_codes: [],
        updated_at: new Date()?.toISOString()
      })?.eq('id', userId);

    if (error) throw error;

    return { success: true };
  },

  /**
   * Update 2FA method
   */
  async update2FAMethod(userId, method) {
    if (!['email', 'sms']?.includes(method)) {
      throw new Error('Invalid 2FA method. Must be email or sms');
    }

    // Check verification status
    const { data: profile } = await supabase?.from('user_profiles')?.select('email_verified, phone_verified, phone')?.eq('id', userId)?.single();

    if (method === 'email' && !profile?.email_verified) {
      throw new Error('Email must be verified before switching to email-based 2FA');
    }

    if (method === 'sms') {
      if (!profile?.phone) {
        throw new Error('Phone number must be added before switching to SMS-based 2FA');
      }
      if (!profile?.phone_verified) {
        throw new Error('Phone number must be verified before switching to SMS-based 2FA');
      }
    }

    const { error } = await supabase?.from('user_profiles')?.update({
        two_factor_method: method,
        updated_at: new Date()?.toISOString()
      })?.eq('id', userId);

    if (error) throw error;

    return { success: true, method };
  },

  /**
   * Generate backup codes
   */
  async generateBackupCodes(userId) {
    const { data, error } = await supabase?.rpc('generate_2fa_backup_codes', {
      p_user_id: userId
    });

    if (error) throw error;

    return data || [];
  },

  /**
   * Create 2FA session and send verification code
   */
  async create2FASession(userId, method = 'email') {
    try {
      // Get user profile to fetch contact info
      const { data: profile, error: profileError } = await supabase?.from('user_profiles')?.select('email, phone, full_name')?.eq('id', userId)?.single();

      if (profileError) throw profileError;

      // Generate session token and verification code
      const sessionToken = crypto?.randomUUID();
      const verificationCode = Math.floor(100000 + Math.random() * 900000)?.toString();
      const expiresAt = new Date(Date.now() + 15 * 60 * 1000); // 15 minutes

      // Store 2FA session
      const { data, error } = await supabase?.from('two_factor_sessions')?.insert({
          user_id: userId,
          session_token: sessionToken,
          verification_code: verificationCode,
          delivery_method: method,
          expires_at: expiresAt?.toISOString()
        })?.select()?.single();

      if (error) throw error;

      // Send verification code based on method
      if (method === 'email') {
        try {
          await supabase?.functions?.invoke('send-transactional-email', {
            body: {
              to: profile?.email,
              type: '2fa_code',
              data: { code: verificationCode }
            }
          });
        } catch (emailErr) {
          console.error('2FA email sending failed:', emailErr);
          // Continue - code is still valid
        }
      } else if (method === 'sms') {
        // TODO: Implement SMS sending logic
      }

      return {
        sessionId: data?.id,
        sessionToken,
        expiresAt
      };
    } catch (error) {
      throw error;
    }
  },

  /**
   * Verify 2FA code and complete login
   */
  async verify2FACode(sessionToken, verificationCode) {
    // Get session
    const { data: session, error: sessionError } = await supabase?.from('two_factor_auth_sessions')?.select('*, user_verifications(*)')?.eq('session_token', sessionToken)?.single();

    if (sessionError || !session) {
      throw new Error('Invalid or expired session');
    }

    // Check session expiration
    if (new Date(session.expires_at) < new Date()) {
      throw new Error('Session expired. Please try logging in again');
    }

    // Verify the code
    const { error: verifyError } = await supabase?.from('user_verifications')?.update({
        status: 'verified',
        verified_at: new Date()?.toISOString(),
        updated_at: new Date()?.toISOString()
      })?.eq('id', session?.verification_id)?.eq('verification_code', verificationCode)?.eq('status', 'pending');

    if (verifyError) {
      // Increment attempts
      await supabase?.from('user_verifications')?.update({
          attempts: (session?.user_verifications?.attempts || 0) + 1
        })?.eq('id', session?.verification_id);

      throw new Error('Invalid verification code');
    }

    // Mark session as verified
    const { error: updateError } = await supabase?.from('two_factor_auth_sessions')?.update({
        verified: true,
        verified_at: new Date()?.toISOString()
      })?.eq('id', session?.id);

    if (updateError) throw updateError;

    return {
      success: true,
      userId: session?.user_id
    };
  },

  /**
   * Verify backup code
   */
  async verifyBackupCode(sessionToken, backupCode) {
    // Get session
    const { data: session, error: sessionError } = await supabase?.from('two_factor_auth_sessions')?.select('*')?.eq('session_token', sessionToken)?.single();

    if (sessionError || !session) {
      throw new Error('Invalid or expired session');
    }

    // Check session expiration
    if (new Date(session.expires_at) < new Date()) {
      throw new Error('Session expired. Please try logging in again');
    }

    // Get user's backup codes
    const { data: profile, error: profileError } = await supabase?.from('user_profiles')?.select('two_factor_backup_codes')?.eq('id', session?.user_id)?.single();

    if (profileError || !profile) {
      throw new Error('User not found');
    }

    // Check if backup code is valid
    const backupCodes = profile?.two_factor_backup_codes || [];
    if (!backupCodes?.includes(backupCode)) {
      throw new Error('Invalid backup code');
    }

    // Remove used backup code
    const updatedCodes = backupCodes?.filter(code => code !== backupCode);
    await supabase?.from('user_profiles')?.update({
        two_factor_backup_codes: updatedCodes
      })?.eq('id', session?.user_id);

    // Mark session as verified
    const { error: updateError } = await supabase?.from('two_factor_auth_sessions')?.update({
        verified: true,
        verified_at: new Date()?.toISOString()
      })?.eq('id', session?.id);

    if (updateError) throw updateError;

    return {
      success: true,
      userId: session?.user_id,
      remainingCodes: updatedCodes?.length
    };
  },

  /**
   * Get user's backup codes
   */
  async getBackupCodes(userId) {
    const { data, error } = await supabase?.from('user_profiles')?.select('two_factor_backup_codes')?.eq('id', userId)?.single();

    if (error) throw error;

    return data?.two_factor_backup_codes || [];
  },

  /**
   * Helper: Generate random session token
   */
  _generateSessionToken() {
    return Array.from(crypto.getRandomValues(new Uint8Array(32)))?.map(b => b?.toString(16)?.padStart(2, '0'))?.join('');
  },

  /**
   * Helper: Get user's IP address (simplified)
   */
  async _getIPAddress() {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response?.json();
      return data?.ip;
    } catch {
      return 'unknown';
    }
  }
};